import React from "react";

import { useModifiers } from "@v2/features/modifiers/hooks/useModifiers";
import {
  useDefaultSelectedElementDesignation,
  useDisplayMode,
  useIsDeleting,
  useModifiersStoreActions,
} from "@v2/features/modifiers/store/ModifiersStoreContext";
import { useReploHotkeys } from "@v2/hooks/useHotkeys";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

interface ReactEditorGlobalHotkeysListenerProps {
  containerRef: React.RefObject<HTMLDivElement>;
}

const ReactEditorGlobalHotkeysListener: React.FC<
  ReactEditorGlobalHotkeysListenerProps
> = ({ containerRef }) => {
  const displayMode = useDisplayMode();
  const defaultDesignation = useDefaultSelectedElementDesignation();
  const isDeleting = useIsDeleting();
  const modifierActions = useModifiersStoreActions();

  const { applyModifier, isProcessing: isPending } = useModifiers();

  const handleDelete = React.useCallback(() => {
    const isInSelectMode = displayMode === "select";
    const isNotLoading = !isPending;
    const isNotTextElement = defaultDesignation?.element.type !== "text";

    const canDeleteElement =
      isInSelectMode && isNotLoading && isNotTextElement && !isDeleting;

    if (canDeleteElement && defaultDesignation) {
      // NOTE (Nick, 2025-12-11): Set isDeleting in store so the Modifiers panel
      // shows the disabled state and prevents spam clicks
      modifierActions.setIsDeleting(true);

      void applyModifier({
        modifier: { type: "delete", value: null },
        stableElementId: defaultDesignation.element.stableElementId,
      });
    }
  }, [
    displayMode,
    defaultDesignation,
    isPending,
    isDeleting,
    modifierActions,
    applyModifier,
  ]);

  useReploHotkeys(
    {
      delete: handleDelete,
    },
    containerRef.current,
  );

  // Note (Noah, 2025-09-18): Whenever the default selected element changes, put focus on the
  // editor container so that keyboard events work
  useOnValueChange(defaultDesignation, (newDesignation, oldDesignation) => {
    if (
      oldDesignation?.element.stableElementId !==
        newDesignation?.element.stableElementId &&
      newDesignation &&
      containerRef.current
    ) {
      containerRef.current.focus();
    }
  });

  return null;
};

export default ReactEditorGlobalHotkeysListener;
